<template>
  <div
    v-show="showSortAndFilterContainer"
    class="sort-and-filter-container is-hidden-tablet"
    :style="{ top: `${headerClientHeight}px` }"
  >
    <SelectBox
      :options="selectBoxSortByOptions"
      :placeholder-text="$t('products.sort').toUpperCase()"
      @change="onSortChange"
    />
    <Button class="filter-btn" @click="onFilterClick">
      {{ $t('products.filters.title') }}
      <Icons
        v-if="isFilterEnabled"
        name="checkmark"
        aria-label="filter-state"
        style="--icon-size: 20px"
      />
    </Button>
    <FilterModalContainer
      :entity-type="entityType"
      @close-modal="onCloseClick"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Button from '@sephora-asia/core-button/Button.vue'
import Icons from '@sephora-asia/atoms-icons/Icons.vue'
import isEmpty from 'lodash/isEmpty'
import SelectBox from '@sephora-asia/inputs-select-box/SelectBox.vue'
import i18n from '../mixins/i18n'
import productFiltersHelper from '../mixins/productFiltersHelper'
import FilterModalContainer from './FilterModalContainer.vue'

export default {
  name: 'SortAndFilterContainer',
  components: {
    Button,
    Icons,
    FilterModalContainer,
    SelectBox
  },

  mixins: [i18n, productFiltersHelper],

  props: {
    /**
     * Page entity type
     */
    entityType: {
      type: String,
      required: true
    },
    /**
     * Options for sorting
     */
    sortByOptions: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      headerClientHeight: 0,
      mutationObserver: undefined,
      resizeObserver: undefined,
      showFilter: false,
      showSortAndFilterContainer: false
    }
  },

  computed: {
    ...mapGetters({
      initialFilterUrl: 'listingPageFilters/initialFilterUrl',
      showFiltersMobile: 'listingPageFilters/showFiltersMobile'
    }),

    selectBoxSortByOptions() {
      let defaultOptionValue = 'sales'

      if (this.entityType === 'search') {
        defaultOptionValue = 'relevance'
      }

      this.sortByOptions.find(
        (option) => option.value === defaultOptionValue
      ).isSelected = false
      return [...this.sortByOptions]
    },
    isFilterEnabled() {
      // Removing this query parameters except mobile filters
      const query = this.filterQueryParams(
        'sort',
        'page',
        'view',
        'language',
        'q'
      )
      return !isEmpty(query)
    }
  },

  mounted() {
    const headerElement = globalThis.document.getElementsByClassName(
      'section is-paddingless is-sticky'
    )[0]

    this.getDimensions()
    globalThis.document.getElementsByTagName('main')[0].style.marginTop = '54px'

    this.mutationObserver = new MutationObserver(() => {
      this.getDimensions()
    })
    this.resizeObserver = new ResizeObserver(() => {
      this.getDimensions()
    })

    this.mutationObserver.observe(headerElement, {
      subtree: true,
      childList: true
    })
    this.resizeObserver.observe(headerElement)
    this.showSortAndFilterContainer = true
  },

  destroyed() {
    globalThis.document
      .getElementsByTagName('main')[0]
      .style.removeProperty('margin-top')
    this.resizeObserver.disconnect()
    this.mutationObserver.disconnect()
  },

  methods: {
    getDimensions() {
      this.headerClientHeight = globalThis.document.getElementsByClassName(
        'section is-paddingless is-sticky'
      )[0]?.clientHeight
    },

    onSortChange(event) {
      this.$emit('sort-change', event)
    },

    onFilterClick() {
      // To update default query parameters except page and view in store
      // Ensures if user opens & closes modal then sort filter will remain as it is in query.
      this.updateListingPageInitialFilterUrl()
      this.$store.commit('listingPageFilters/setShowFiltersMobile', true)
    },

    onCloseClick() {
      this.$store.commit('listingPageFilters/setShowFiltersMobile', false)
    },

    updateListingPageInitialFilterUrl() {
      // To retain default query parameters except page, view
      const query = this.filterQueryParams('page', 'view')

      // In some cases, we are getting undefined value for sort in query. In other cases, we need to
      // retain sort parameter
      if (!query.sort) delete query.sort
      // Making a copy of filter url just before opening filter modal
      this.$store.commit('listingPageFilters/setListingPageInitialFilterUrl', {
        query,
        params: this.$route.params
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.sort-and-filter-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 20px;
  padding: 7px 10px;
  background-color: $grey-light;
  position: fixed;
  left: 0;
  right: 0;
  bottom: auto;
  z-index: $zindex-fixed;

  ::v-deep .filter-btn {
    // suggestion: create new btn style in ui-component
    width: 100%;
    padding: 2px 0 0;
    background-color: $white;
    border: 1px solid $grey;
    font-weight: 500;
    color: $black;
    text-transform: uppercase;
  }

  ::v-deep .select {
    width: 100%;
    padding: 2px 15px 0;
    background-color: $white;
    border: 1px solid $grey;
    font-size: 16px;
    font-weight: 500;
    text-align: center;

    option {
      text-align: center;
    }
  }
}

.fixed .sort-and-filter-container {
  z-index: $zindex-menu-container;
}
</style>
